/**
 * Checkout GraphQL Mutation
 * Handles cartBuyerIdentityUpdate and cartAttributesUpdate mutations
 * for delivery and pickup preferences
 */

/**
 * Gets the Storefront API access token
 */
function getStorefrontAccessToken() {
  if (window.Shopify?.storefrontAccessToken) {
    return window.Shopify.storefrontAccessToken;
  }
  
  const metaToken = document.querySelector('meta[name="shopify-storefront-access-token"]');
  if (metaToken) {
    return metaToken.getAttribute('content');
  }
  
  const bodyToken = document.body?.dataset?.storefrontToken || 
                    document.documentElement?.dataset?.storefrontToken;
  if (bodyToken) {
    return bodyToken;
  }
  
  return null;
}

/**
 * Gets cart token from cookie
 */
function getCartTokenFromCookie() {
  // Try to get from cart cookie
  const cartCookie = document.cookie.split('; ').find(row => row.startsWith('cart='));
  if (cartCookie) {
    const encodedToken = cartCookie.split('=')[1];
    if (encodedToken) {
      // URL decode the token (cookie contains encoded version)
      try {
        return decodeURIComponent(encodedToken);
      } catch (error) {
        console.error('Failed to decode cart token:', error);
        // If decoding fails, try using it as-is
        return encodedToken;
      }
    }
  }
  
  // Try to get from Shopify cart object
  if (window.Shopify?.cart?.token) {
    return window.Shopify.cart.token;
  }
  
  // Fallback: fetch from /cart.js
  return null;
}

/**
 * Gets Storefront API cart ID from cookie
 * Uses the cart token from cookie to construct the Storefront API cart ID
 */
function getStorefrontCartId() {
  // Get cart token from cookie (URL-decoded)
  const cartToken = getCartTokenFromCookie();
  
  if (!cartToken) {
    throw new Error('Cart token not found in cookie. Please ensure you have items in your cart.');
  }

  // Construct Storefront API cart ID from decoded token
  // Format: gid://shopify/Cart/{token}
  return `gid://shopify/Cart/${cartToken}`;
}

/**
 * Creates a new Storefront API cart from current cart items
 */
async function createStorefrontCart() {
  const STOREFRONT_CART_KEY = 'shopify_storefront_cart_id';
  const accessToken = getStorefrontAccessToken();
  if (!accessToken) {
    throw new Error('Storefront API access token not found');
  }

  // Get the current cart items from the regular cart
  let cartItems = [];
  try {
    const cartResponse = await fetch('/cart.js');
    const cart = await cartResponse.json();
    
    if (cart.items && cart.items.length > 0) {
      cartItems = cart.items.map(item => ({
        merchandiseId: `gid://shopify/ProductVariant/${item.variant_id}`,
        quantity: item.quantity,
        attributes: item.properties ? Object.entries(item.properties).map(([key, value]) => ({
          key: key,
          value: String(value)
        })) : []
      }));
    }
  } catch (error) {
    console.error('Failed to fetch cart items:', error);
    throw new Error('Failed to fetch cart items');
  }

  if (cartItems.length === 0) {
    throw new Error('Cart is empty. Please add items to your cart first.');
  }

  // Create a cart using Storefront API
  const mutation = `
    mutation cartCreate($input: CartInput!) {
      cartCreate(input: $input) {
        cart {
          id
        }
        userErrors {
          field
          message
        }
      }
    }
  `;

  const variables = {
    input: {
      lines: cartItems
    }
  };

  try {
    const result = await makeGraphQLRequest(mutation, variables);
    
    if (result.data?.cartCreate?.userErrors?.length > 0) {
      const errors = result.data.cartCreate.userErrors.map(e => e.message).join(', ');
      throw new Error(`Failed to create cart: ${errors}`);
    }

    if (result.data?.cartCreate?.cart?.id) {
      // Store the cart ID for future use
      localStorage.setItem(STOREFRONT_CART_KEY, result.data.cartCreate.cart.id);
      return result.data.cartCreate.cart.id;
    }

    throw new Error('Failed to create cart: No cart ID returned');
  } catch (error) {
    console.error('Failed to create Storefront API cart:', error);
    throw error;
  }
}

/**
 * Gets customer email
 */
function getCustomerEmail() {
  // Check if customer is logged in and email is available
  if (window.Shopify?.customer?.email) {
    return window.Shopify.customer.email;
  }
  
  // Check for email in meta tag or data attribute
  const emailMeta = document.querySelector('meta[name="customer-email"]');
  if (emailMeta) {
    return emailMeta.getAttribute('content');
  }
  
  // Try to get from customer object if available in Liquid
  const customerEmailEl = document.getElementById('gameball-customerEmail');
  if (customerEmailEl && customerEmailEl.value) {
    return customerEmailEl.value;
  }
  
  return null;
}

/**
 * Gets pickup location ID
 * For now, we'll need to get it from shop locations or use a default
 * You may need to pass this as a data attribute in the liquid file
 */
function getPickupLocationId() {
  // Check for location ID in data attribute
  const pickupSelector = document.querySelector('delivery-pickup-selector');
  if (pickupSelector?.dataset?.pickupLocationId) {
    return pickupSelector.dataset.pickupLocationId;
  }
  
  // Check for location ID in meta tag
  const locationMeta = document.querySelector('meta[name="shopify-location-id"]');
  if (locationMeta) {
    return `gid://shopify/Location/${locationMeta.getAttribute('content')}`;
  }
  
  // Default location ID - you may need to update this
  // This should come from your shop's location data
  return null;
}

/**
 * Gets pickup location coordinates
 * For UAE, default coordinates or from location data
 */
function getPickupCoordinates() {
  // Check for coordinates in data attributes
  const pickupSelector = document.querySelector('delivery-pickup-selector');
  if (pickupSelector?.dataset?.pickupLatitude && pickupSelector?.dataset?.pickupLongitude) {
    return {
      latitude: parseFloat(pickupSelector.dataset.pickupLatitude),
      longitude: parseFloat(pickupSelector.dataset.pickupLongitude),
      countryCode: pickupSelector.dataset.pickupCountryCode || 'AE'
    };
  }
  
  // Default coordinates for UAE (Dubai) - you may need to update this
  return {
    latitude: 25.2048,
    longitude: 55.2708,
    countryCode: 'AE'
  };
}

/**
 * Makes a GraphQL request to Shopify Storefront API
 */
async function makeGraphQLRequest(query, variables = {}) {
  const accessToken = getStorefrontAccessToken();
  
  if (!accessToken) {
    throw new Error('Storefront API access token not found');
  }

  try {
    const apiVersion = '2024-01';
    const response = await fetch(`/api/${apiVersion}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': accessToken,
      },
      body: JSON.stringify({
        query: query,
        variables: variables,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
    }

    const data = await response.json();
    
    if (data.errors) {
      throw new Error(`GraphQL errors: ${JSON.stringify(data.errors, null, 2)}`);
    }

    return data;
  } catch (error) {
    console.error('GraphQL request failed:', error);
    throw error;
  }
}

/**
 * Executes checkout mutation for delivery
 */
async function executeDeliveryMutation(cartId, deliveryAddress, attributes, customerEmail = null, customerAccessToken = null) {
  const mutation = `
    mutation checkoutDelivery(
      $cartId: ID!
      $email: String
      $customerAccessToken: String
      $deliveryAddress: MailingAddressInput!
      $attributes: [AttributeInput!]!
    ) {
      cartAttributesUpdate(cartId: $cartId, attributes: $attributes) {
        userErrors {
          field
          message
        }
        cart {
          id
        }
      }
      cartBuyerIdentityUpdate(
        cartId: $cartId
        buyerIdentity: {
          email: $email
          customerAccessToken: $customerAccessToken
          deliveryAddressPreferences: [{ deliveryAddress: $deliveryAddress }]
          preferences: {
            delivery: {
              deliveryMethod: [SHIPPING]
              pickupHandle: []
              coordinates: null
            }
          }
        }
      ) {
        userErrors {
          field
          message
        }
        cart {
          id
          buyerIdentity {
            email
            deliveryAddressPreferences {
              ... on MailingAddress {
                address1
                address2
                city
                province
                country
                zip
                firstName
                lastName
                phone
              }
            }
          }
        }
      }
    }
  `;

  // Ensure attributes is always an array
  const attributesArray = Array.isArray(attributes) && attributes.length > 0
    ? attributes.map(attr => ({
        key: attr.key,
        value: attr.value
      }))
    : [];

  const variables = {
    cartId: cartId,
    email: customerEmail,
    customerAccessToken: customerAccessToken,
    deliveryAddress: {
      address1: deliveryAddress.address1,
      address2: deliveryAddress.address2 || null,
      city: deliveryAddress.city,
      province: deliveryAddress.province || null,
      country: deliveryAddress.country,
      zip: deliveryAddress.zip || null,
      firstName: deliveryAddress.firstName || null,
      lastName: deliveryAddress.lastName || null,
      phone: deliveryAddress.phone || null,
    },
    attributes: attributesArray
  };

  return await makeGraphQLRequest(mutation, variables);
}

/**
 * Executes checkout mutation for pickup
 */
async function executePickupMutation(cartId, pickupLocationId, coordinates, customerEmail = null, customerAccessToken = null) {
  const mutation = `
    mutation checkoutPickup(
      $cartId: ID!
      $email: String
      $customerAccessToken: String
      $pickupHandles: [String!]!
      $coordinates: CartDeliveryCoordinatesPreferenceInput
    ) {
      cartBuyerIdentityUpdate(
        cartId: $cartId
        buyerIdentity: {
          email: $email
          customerAccessToken: $customerAccessToken
          deliveryAddressPreferences: null
          preferences: {
            delivery: {
              deliveryMethod: [PICK_UP]
              pickupHandle: $pickupHandles
              coordinates: $coordinates
            }
          }
        }
      ) {
        userErrors {
          field
          message
        }
        cart {
          id
          buyerIdentity {
            email
          }
        }
      }
    }
  `;

  // Extract the location ID part from the GID (remove "gid://shopify/Location/" prefix)
  // pickupHandle expects just the ID as a string, not the full GID
  let locationHandle = pickupLocationId;
  if (pickupLocationId && pickupLocationId.startsWith('gid://shopify/Location/')) {
    locationHandle = pickupLocationId.replace('gid://shopify/Location/', '');
  }

  const variables = {
    cartId: cartId,
    email: customerEmail,
    customerAccessToken: customerAccessToken,
    pickupHandles: [locationHandle], // Array of location handles as strings
    coordinates: coordinates ? {
      latitude: coordinates.latitude,
      longitude: coordinates.longitude,
      countryCode: coordinates.countryCode
    } : null
  };

  return await makeGraphQLRequest(mutation, variables);
}

/**
 * Converts time string like "2PM" or "2:30PM" to integer format (e.g., 1400 or 1430)
 * Examples: "2PM" -> 1400, "2:30PM" -> 1430, "12AM" -> 0, "12:30PM" -> 1230
 */
function parseTimeToInteger(timeStr) {
  if (!timeStr) return 0;
  
  // Remove whitespace and convert to uppercase
  timeStr = timeStr.trim().toUpperCase();
  
  // Check if it's AM or PM
  const isPM = timeStr.includes('PM');
  const isAM = timeStr.includes('AM');
  
  // Remove AM/PM
  timeStr = timeStr.replace(/[AP]M/i, '').trim();
  
  // Split by colon to get hours and minutes
  const parts = timeStr.split(':');
  let hours = parseInt(parts[0], 10) || 0;
  const minutes = parseInt(parts[1], 10) || 0;
  
  // Convert to 24-hour format
  if (isPM && hours !== 12) {
    hours += 12;
  } else if (isAM && hours === 12) {
    hours = 0;
  }
  
  // Convert to integer format: HHMM (e.g., 14:30 -> 1430, 2:00 -> 200)
  return hours * 100 + minutes;
}

/**
 * Parses time slot string like "2PM - 6PM" and extracts start and end times as integers
 */
function parseTimeSlot(timeSlotStr) {
  if (!timeSlotStr) return { start_time: 0, end_time: 0 };
  
  // Split by " - " to get start and end times
  const parts = timeSlotStr.split(' - ');
  if (parts.length !== 2) {
    console.error('Invalid time slot format:', timeSlotStr);
    return { start_time: 0, end_time: 0 };
  }
  
  const startTime = parseTimeToInteger(parts[0].trim());
  const endTime = parseTimeToInteger(parts[1].trim());
  
  return { start_time: startTime, end_time: endTime };
}

/**
 * Main function to execute checkout mutation based on mode
 */
async function executeCheckoutMutation(options) {
  const {
    mode, // 'delivery' or 'pickup'
    deliveryAddress, // For delivery mode
    attributes, // For delivery mode: { delivery_date, delivery_time }
    pickupLocationId, // For pickup mode
    coordinates, // For pickup mode
    customerEmail,
    customerAccessToken
  } = options;

  // Get Storefront API cart ID (reuse existing or create new if needed)
  let cartId;
  try {
    cartId = getStorefrontCartId();
  } catch (error) {
    console.error('Failed to get Storefront cart ID:', error);
    throw error;
  }

  // Get customer email if not provided
  const email = customerEmail || getCustomerEmail();

  if (mode === 'delivery') {
    if (!deliveryAddress) {
      throw new Error('Delivery address is required for delivery mode');
    }

    // Format attributes as separate attributes, date would always be present, time would be present if selected (time slot)
    const mutationAttributes = [];
    
    // Always add delivery_slot_date if date is provided
    if (attributes?.delivery_date) {
      mutationAttributes.push({
        key: 'delivery_slot_date',
        value: attributes.delivery_date
      });
    }
    
    // Add start and end time attributes only if timeslot is selected
    if (attributes?.delivery_time) {
      // Parse the time slot to get start_time and end_time as integers
      const { start_time, end_time } = parseTimeSlot(attributes.delivery_time);
      
      // Convert to strings and add as separate attributes
      mutationAttributes.push({
        key: 'delivery_slot_start',
        value: String(start_time)
      });
      
      mutationAttributes.push({
        key: 'delivery_slot_end',
        value: String(end_time)
      });
    }

    return await executeDeliveryMutation(cartId, deliveryAddress, mutationAttributes, email, customerAccessToken);
  } else if (mode === 'pickup') {
    const locationId = pickupLocationId || getPickupLocationId();
    if (!locationId) {
      throw new Error('Pickup location ID is required for pickup mode');
    }

    const coords = coordinates || getPickupCoordinates();
    
    return await executePickupMutation(cartId, locationId, coords, email, customerAccessToken);
  } else {
    throw new Error(`Invalid mode: ${mode}. Must be 'delivery' or 'pickup'`);
  }
}

// Make functions available globally
window.executeCheckoutMutation = executeCheckoutMutation;
window.getCustomerEmail = getCustomerEmail;
window.getPickupLocationId = getPickupLocationId;
window.getPickupCoordinates = getPickupCoordinates;

